/**
 * WhatsApp inbox API client (PROD-1849): the Notifications routes under
 * `/api/Notifications/whatsapp/inbox/...`, on the same gateway as
 * `/api/Notifications/whatsapp/credentials` (see use-whatsapp-channel.ts).
 *
 * Contract: whatsapp-inbox-api-contract-2026-10-23 (backend owner, verbatim).
 *   1. GET  /conversations?status&take&cursor&updatedAfter → { items, nextCursor }
 *   2. GET  /conversations/{id}/messages?take&cursor&updatedAfter → { items (newest first), nextCursor (older) }
 *   3. POST /conversations/{id}/read → 204
 *   4. POST /conversations/{id}/messages { body, clientMessageId } → 202 Message
 *      (next backend PR; until then a 404/405 reads as "not available yet").
 * Template send from the inbox: PROD-3341 (below), behind `?x=inbox-templates`.
 *
 * Every call goes through the shared `fetchWithAuth` (the user's bearer token and
 * the usual header set; the org comes from the token's `orgId` claim). The raw
 * `Response` is needed for the `X-Server-Time` header, the polling clock.
 *
 * Bodies are camelCase JSON (Newtonsoft); null fields arrive as `null`, not
 * omitted (contract additions, 23 Oct). They are parsed with the shared v2.1
 * mappers (../inbox-types.ts): unknown enums degrade safely and extra fields are
 * ignored. A 403 is `{ "error": "organization_required" }` (no orgId) or a plain
 * 403 with no body (no permission). Routes 1-3 are Notifications PR #281.
 */
import { fetchWithAuth } from '@/shared/api/api-client';
import {
  mapConversation,
  mapMessage,
  type Conversation,
  type ConversationStatus,
  type Message,
  type Raw,
} from '../inbox-types';

export const INBOX_BASE = '/api/Notifications/whatsapp/inbox';

/** The most items one page may carry (contract: take 1..100). */
export const INBOX_MAX_TAKE = 100;

/** A failed inbox call: the HTTP status and the contract's `{ "error": "<code>" }` (null when absent). */
export class InboxApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(status: number, code: string | null) {
    super(`WhatsApp inbox API ${status}${code ? ` ${code}` : ''}`);
    this.name = 'InboxApiError';
    this.status = status;
    this.code = code;
  }
}

/** Reads the `{ "error": "<code>" }` body. A body that is not JSON keeps the status and has no code. */
export function toInboxError(status: number, body: string): InboxApiError {
  try {
    const parsed = JSON.parse(body) as { error?: unknown };
    return new InboxApiError(status, typeof parsed?.error === 'string' ? parsed.error : null);
  } catch {
    return new InboxApiError(status, null);
  }
}

/** One page of a list route, plus the server clock read from `X-Server-Time`. */
export interface InboxPage<T> {
  items: T[];
  nextCursor: string | null;
  /** `X-Server-Time` (ISO), or null when the header is missing or unreadable. */
  serverTime: string | null;
}

/** The `X-Server-Time` header, when it is a parseable instant. */
export function readServerTime(res: Response): string | null {
  const value = res.headers.get('X-Server-Time');
  return value && Number.isFinite(Date.parse(value)) ? value : null;
}

async function send(endpoint: string, init: RequestInit): Promise<Response> {
  const res = await fetchWithAuth(endpoint, init);
  if (!res.ok) throw toInboxError(res.status, await res.text());
  return res;
}

async function readPage<T>(res: Response, map: (raw: Raw) => T): Promise<InboxPage<T>> {
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  const r = typeof body === 'object' && body !== null ? (body as Raw) : {};
  const items = Array.isArray(r.items)
    ? r.items.filter((x): x is Raw => typeof x === 'object' && x !== null).map(map)
    : [];
  return {
    items,
    nextCursor: typeof r.nextCursor === 'string' && r.nextCursor ? r.nextCursor : null,
    serverTime: readServerTime(res),
  };
}

function query(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

const seg = encodeURIComponent;

export interface PageRequest {
  take?: number;
  cursor?: string | null;
  /** Polling: only items changed after this instant (ISO, the last `X-Server-Time` seen). */
  updatedAfter?: string | null;
  signal?: AbortSignal;
}

/** 1) `GET /inbox/conversations`. Sorted by lastMessageAt desc, then id (server side). */
export async function fetchConversations(
  req: PageRequest & { status?: ConversationStatus } = {},
): Promise<InboxPage<Conversation>> {
  const endpoint = `${INBOX_BASE}/conversations${query({
    status: req.status,
    take: req.take,
    cursor: req.cursor,
    updatedAfter: req.updatedAfter,
  })}`;
  return readPage(await send(endpoint, { method: 'GET', signal: req.signal }), mapConversation);
}

/** 2) `GET /inbox/conversations/{id}/messages`. Items come NEWEST FIRST; `nextCursor` pages to older ones. */
export async function fetchMessages(
  conversationId: string,
  req: PageRequest = {},
): Promise<InboxPage<Message>> {
  const endpoint = `${INBOX_BASE}/conversations/${seg(conversationId)}/messages${query({
    take: req.take,
    cursor: req.cursor,
    updatedAfter: req.updatedAfter,
  })}`;
  return readPage(await send(endpoint, { method: 'GET', signal: req.signal }), mapMessage);
}

/** 3) `POST /inbox/conversations/{id}/read` (no body) → 204. */
export async function markConversationRead(conversationId: string): Promise<void> {
  await send(`${INBOX_BASE}/conversations/${seg(conversationId)}/read`, { method: 'POST' });
}

export interface StaffReplyRequest {
  /** 1..4096 characters. */
  body: string;
  /** A uuid; the same id again returns the first result and does not send twice. */
  clientMessageId: string;
}

/** 4) `POST /inbox/conversations/{id}/messages` → 202 `Message` (provenance human-staff, deliveryStatus queued). */
export async function sendStaffReply(conversationId: string, req: StaffReplyRequest): Promise<Message> {
  const res = await send(`${INBOX_BASE}/conversations/${seg(conversationId)}/messages`, {
    method: 'POST',
    body: JSON.stringify({ body: req.body, clientMessageId: req.clientMessageId }),
  });
  const text = await res.text();
  let raw: unknown = null;
  try {
    raw = text ? JSON.parse(text) : null;
  } catch {
    raw = null;
  }
  return mapMessage(typeof raw === 'object' && raw !== null ? (raw as Raw) : {});
}

// ---------------------------------------------------------------------------
// PROD-3341: approved templates when the 24-hour window is closed (Notifications #289)
// ---------------------------------------------------------------------------

/**
 * One template the clinic can send in this conversation, every value filled by the server: from the upcoming
 * appointment, or (no upcoming appointment, Notifications #290) a general follow-up with the patient's first name and
 * the clinic's name, whose list has `appointmentId: null`.
 */
export interface InboxTemplate {
  templateKey: string;
  label: string;
  language: string;
  /** The text the patient will read. */
  preview: string;
  variables: { name: string; value: string; editable: boolean }[];
}

/** Why a conversation offers no template. */
export type InboxTemplatesReason = 'no_patient' | 'no_upcoming_appointment' | 'no_template_ready' | 'appointment_unreadable';

export interface InboxTemplateList {
  appointmentId: string | null;
  reason: InboxTemplatesReason | null;
  templates: InboxTemplate[];
}

const REASONS: readonly InboxTemplatesReason[] = ['no_patient', 'no_upcoming_appointment', 'no_template_ready', 'appointment_unreadable'];

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

function mapTemplate(raw: Raw): InboxTemplate {
  const vars = Array.isArray(raw.variables) ? raw.variables : [];
  return {
    templateKey: str(raw.templateKey),
    label: str(raw.label) || str(raw.templateKey),
    language: str(raw.language) || 'en',
    preview: str(raw.preview),
    variables: vars
      .filter((v): v is Raw => typeof v === 'object' && v !== null)
      .map((v) => ({ name: str(v.name), value: str(v.value), editable: v.editable === true })),
  };
}

/**
 * `GET .../conversations/{id}/templates` → the templates of the conversation's next upcoming appointment; with none,
 * the general follow-ups (`appointmentId: null`, Notifications #290). An empty list carries `reason`: no_patient,
 * no_upcoming_appointment, no_template_ready or appointment_unreadable (an appointment of the thread could not be read,
 * so nothing is offered).
 */
export async function fetchInboxTemplates(conversationId: string, signal?: AbortSignal): Promise<InboxTemplateList> {
  const res = await send(`${INBOX_BASE}/conversations/${seg(conversationId)}/templates`, { method: 'GET', signal });
  const text = await res.text();
  let raw: unknown = null;
  try {
    raw = text ? JSON.parse(text) : null;
  } catch {
    raw = null;
  }
  const r = typeof raw === 'object' && raw !== null ? (raw as Raw) : {};
  const reason = REASONS.find((x) => x === r.reason) ?? null;
  const templates = Array.isArray(r.templates)
    ? r.templates.filter((t): t is Raw => typeof t === 'object' && t !== null).map(mapTemplate).filter((t) => t.templateKey)
    : [];
  return { appointmentId: typeof r.appointmentId === 'string' ? r.appointmentId : null, reason, templates };
}

export interface InboxTemplateSendRequest {
  /** The list's appointmentId (null when the list offers templates of no appointment). */
  appointmentId: string | null;
  templateKey: string;
  language: string;
  /** A uuid; the same id again returns the first result and does not send twice. */
  clientMessageId: string;
}

/** `POST .../conversations/{id}/template-messages` → 202 `Message` (sent, or failed with Meta's reason). */
export async function sendInboxTemplate(conversationId: string, req: InboxTemplateSendRequest): Promise<Message> {
  const res = await send(`${INBOX_BASE}/conversations/${seg(conversationId)}/template-messages`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
  const text = await res.text();
  let raw: unknown = null;
  try {
    raw = text ? JSON.parse(text) : null;
  } catch {
    raw = null;
  }
  return mapMessage(typeof raw === 'object' && raw !== null ? (raw as Raw) : {});
}

// ---------------------------------------------------------------------------
// PROD-3335: patient files and photos (additive contract; backend not merged yet)
// ---------------------------------------------------------------------------

/** `.../conversations/{cid}/messages/{mid}/attachment`. */
export function attachmentPath(conversationId: string, messageId: string): string {
  return `${INBOX_BASE}/conversations/${seg(conversationId)}/messages/${seg(messageId)}/attachment`;
}

/**
 * `GET .../attachment` → the file's bytes (404 unless the attachment is `stored`). `download: true` adds
 * `?download=true` (sent as a download; Notifications #286). Auth is the bearer token, so the page reads the bytes here and shows
 * them through an object URL: a plain `<img src>` or `<a href>` would not authenticate.
 */
export async function fetchAttachment(
  conversationId: string,
  messageId: string,
  opts: { download?: boolean; signal?: AbortSignal } = {},
): Promise<Blob> {
  const endpoint = `${attachmentPath(conversationId, messageId)}${opts.download ? '?download=true' : ''}`;
  const res = await send(endpoint, { method: 'GET', signal: opts.signal });
  return res.blob();
}

/** `PUT .../attachment/intake { batchId }` → saves `intake: { status: "sent", batchId }` on the attachment. */
export async function recordAttachmentIntake(conversationId: string, messageId: string, batchId: string): Promise<void> {
  await send(`${attachmentPath(conversationId, messageId)}/intake`, {
    method: 'PUT',
    body: JSON.stringify({ batchId }),
  });
}
